var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { format, addDays, addWeeks, addMonths, differenceInCalendarDays, differenceInCalendarWeeks, differenceInCalendarMonths, isAfter, isBefore, isSameDay, isSameWeek, isSameMonth, startOfDay, startOfWeek, startOfMonth, endOfWeek, endOfMonth, } from "date-fns";
import { formatURLDate, parseURLDate } from "@travel/shared/utils";
import { BoxScroller } from "../../boxes";
import { DateText } from "../../text";
import styles from "./calendar.module.css";
var DEFAULT_DISABLED = [];
export function Calendar(_a) {
    var className = _a.className, _b = _a.value, valueProp = _b === void 0 ? [null, null] : _b, _c = _a.defaultMonth, defaultMonth = _c === void 0 ? startOfMonth(new Date()) : _c, _d = _a.months, months = _d === void 0 ? 1 : _d, _e = _a.minRange, minRange = _e === void 0 ? 2 : _e, _f = _a.maxRange, maxRange = _f === void 0 ? Infinity : _f, _g = _a.scrolls, scrolls = _g === void 0 ? false : _g, _h = _a.disabled, disabled = _h === void 0 ? DEFAULT_DISABLED : _h, onChange = _a.onChange, rest = __rest(_a, ["className", "value", "defaultMonth", "months", "minRange", "maxRange", "scrolls", "disabled", "onChange"]);
    var _j = __read(useState(valueProp), 2), value = _j[0], setValue = _j[1];
    var _k = __read(useState(0), 2), mostVisibleIndex = _k[0], setMostVisibleIndex = _k[1];
    var _l = __read([
        mostVisibleIndex - 2,
        mostVisibleIndex + 4,
    ], 2), minVisibleIndex = _l[0], maxVisibleIndex = _l[1];
    var _m = __read(useState(0), 2), currentRangeIndex = _m[0], setCurrentRangeIndex = _m[1];
    var _o = __read(useState([
        valueProp,
    ]), 2), ranges = _o[0], setRanges = _o[1];
    var _p = __read(ranges[currentRangeIndex], 2), currentRangeStart = _p[0], currentRangeEnd = _p[1];
    var monthsState = useMemo(function () { return computeMonthsState([currentRangeStart, currentRangeEnd]); }, [currentRangeStart === null || currentRangeStart === void 0 ? void 0 : currentRangeStart.valueOf(), currentRangeEnd === null || currentRangeEnd === void 0 ? void 0 : currentRangeEnd.valueOf()]);
    var ref = useRef(null);
    var disabledMonthState = useMemo(function () {
        if (!disabled.length)
            return {};
        return computeMonthsState(disabled[0], "isDisabled");
    }, [disabled]);
    var disabledRangeState = useMemo(function () {
        if (!currentRangeStart || currentRangeEnd || maxRange === Infinity)
            return {};
        return computeMonthsState([addDays(currentRangeStart, maxRange), addMonths(currentRangeStart, 12)], "isDisabled");
    }, [maxRange, currentRangeStart, currentRangeEnd]);
    var disabledState = useMemo(function () { return mergeCalendarMonths(disabledMonthState, disabledRangeState); }, [disabledMonthState, disabledRangeState]);
    var calendarMonths = Array.from(new Array(months)).map(function (_, index) {
        var month = startOfMonth(addMonths(defaultMonth, index));
        return {
            month: month,
            state: monthsState[month.valueOf()],
            disabled: disabledState[month.valueOf()],
        };
    });
    return (_jsx(BoxScroller, __assign({}, rest, { className: classNames(styles["container"], className), scrollerClassName: classNames(styles["months"]), scrolls: scrolls, items: calendarMonths.map(function (month, index) { return (_createElement(CalendarMonth, __assign({}, month, { visible: index >= minVisibleIndex && index <= maxVisibleIndex, onClick: handleOnMonthClick, key: index }))); }), onMostVisibleChange: function (box) {
            if (box === null || box === void 0 ? void 0 : box.index) {
                setMostVisibleIndex(box.index);
            }
        }, ref: function (handle) {
            ref.current = (handle === null || handle === void 0 ? void 0 : handle.el) || null;
        } })));
    function handleOnMonthClick(event) {
        var dayEl = event.nativeEvent.target;
        while (dayEl && dayEl !== ref.current) {
            if (dayEl === null || dayEl === void 0 ? void 0 : dayEl.classList.contains("travel__calendar--day")) {
                if ((dayEl === null || dayEl === void 0 ? void 0 : dayEl.getAttribute("aria-disabled")) === "true")
                    break;
                var nextRanges = __spreadArray([], __read(ranges), false);
                var dayDate = parseURLDate(dayEl.dataset.date);
                if (!currentRangeStart ||
                    currentRangeEnd ||
                    isBefore(dayDate, currentRangeStart) ||
                    (currentRangeStart && isSameDay(currentRangeStart, dayDate))) {
                    nextRanges[currentRangeIndex] = [dayDate, null];
                }
                else {
                    if (Math.abs(differenceInCalendarDays(dayDate, currentRangeStart)) <
                        minRange) {
                        dayDate = addDays(currentRangeStart, minRange - 1);
                    }
                    nextRanges[currentRangeIndex] = [currentRangeStart, dayDate];
                }
                setRanges(nextRanges);
                onChange === null || onChange === void 0 ? void 0 : onChange(nextRanges[currentRangeIndex]);
            }
            dayEl = dayEl.parentNode;
        }
    }
}
export function CalendarMonth(_a) {
    var className = _a.className, month = _a.month, _b = _a.visible, visible = _b === void 0 ? true : _b, state = _a.state, disabled = _a.disabled, onClick = _a.onClick;
    var weeks = useMemo(function () {
        var startMonth = startOfMonth(month);
        var weekCount = differenceInCalendarWeeks(endOfMonth(month), startMonth) + 1;
        return Array.from(new Array(weekCount)).map(function (_, index) {
            return index === 0 ? startMonth : startOfWeek(addWeeks(startMonth, index));
        });
    }, [month.valueOf()]);
    return (_jsxs("div", { className: classNames(styles["month"], className), onClick: onClick, children: [_jsx("div", { className: styles["title"], children: _jsx(DateText, { date: month, format: "MMMM yyyy" }) }), _jsxs("div", { className: styles["head"], "aria-hidden": "true", children: [_jsx("div", { className: styles["day"], children: "S" }), _jsx("div", { className: styles["day"], children: "M" }), _jsx("div", { className: styles["day"], children: "T" }), _jsx("div", { className: styles["day"], children: "W" }), _jsx("div", { className: styles["day"], children: "T" }), _jsx("div", { className: styles["day"], children: "F" }), _jsx("div", { className: styles["day"], children: "S" })] }), weeks.map(function (week, index) {
                var _a, _b;
                return (_jsx(CalendarWeek, { month: month, week: week, state: ((_a = state === null || state === void 0 ? void 0 : state.weeks) === null || _a === void 0 ? void 0 : _a[week.valueOf()]) || {
                        isSelected: !!(state === null || state === void 0 ? void 0 : state.isSelected),
                        isDisabled: !!(state === null || state === void 0 ? void 0 : state.isDisabled),
                        days: {},
                    }, disabled: ((_b = disabled === null || disabled === void 0 ? void 0 : disabled.weeks) === null || _b === void 0 ? void 0 : _b[week.valueOf()]) || {
                        isDisabled: !!(disabled === null || disabled === void 0 ? void 0 : disabled.isDisabled),
                        days: {},
                    } }, week.valueOf()));
            })] }));
}
export function CalendarWeek(_a) {
    var className = _a.className, month = _a.month, week = _a.week, state = _a.state, disabled = _a.disabled;
    var days = useMemo(function () {
        var startWeek = startOfWeek(week);
        return Array.from(new Array(7)).map(function (_, index) {
            return startOfDay(addDays(startWeek, index));
        });
    }, [week.valueOf()]);
    return (_jsx("div", { className: classNames(styles["week"], className), children: days.map(function (day, index) {
            var _a, _b;
            return isBefore(day, month) || isAfter(day, endOfMonth(month)) ? (_jsx("div", { className: styles["empty"] })) : (_jsx(CalendarDay, { day: day, state: ((_a = state === null || state === void 0 ? void 0 : state.days) === null || _a === void 0 ? void 0 : _a[day.valueOf()]) || {
                    isSelected: !!(state === null || state === void 0 ? void 0 : state.isSelected),
                    isDisabled: !!(state === null || state === void 0 ? void 0 : state.isDisabled),
                }, disabled: ((_b = disabled === null || disabled === void 0 ? void 0 : disabled.days) === null || _b === void 0 ? void 0 : _b[day.valueOf()]) || {
                    isDisabled: !!(disabled === null || disabled === void 0 ? void 0 : disabled.isDisabled),
                } }, day.valueOf()));
        }) }));
}
export function CalendarDay(_a) {
    var _b;
    var className = _a.className, day = _a.day, state = _a.state, disabled = _a.disabled;
    return (_jsx("div", { className: classNames(styles["day"], "travel__calendar--day", className, (_b = {},
            _b[styles["is-selected"]] = state === null || state === void 0 ? void 0 : state.isSelected,
            _b[styles["is-range-start"]] = state === null || state === void 0 ? void 0 : state.isRangeStart,
            _b[styles["is-range-end"]] = state === null || state === void 0 ? void 0 : state.isRangeEnd,
            _b)), role: "checkbox", "data-date": formatURLDate(day), "aria-checked": state === null || state === void 0 ? void 0 : state.isSelected, "aria-disabled": disabled === null || disabled === void 0 ? void 0 : disabled.isDisabled, "aria-label": format(day, "d MMMM yyyy"), children: _jsx(DateText, { date: day, format: "d" }) }));
}
function mergeCalendarMonths(s1, s2, prop) {
    var _a;
    if (prop === void 0) { prop = "weeks"; }
    var next = __assign({}, s1);
    for (var month in s2) {
        if (month in s1) {
            next[month] = __assign(__assign(__assign({}, s1[month]), s2[month]), (_a = {}, _a[prop] = mergeCalendarMonths(s1[month][prop], s2[month][prop], "days"), _a));
        }
        else {
            next[month] = s2[month];
        }
    }
    return next;
}
function computeMonthsState(_a, prop) {
    var _b, _c, _d;
    var _e = _a === void 0 ? [null, null] : _a, _f = __read(_e, 2), start = _f[0], end = _f[1];
    if (prop === void 0) { prop = "isSelected"; }
    if (!start)
        return {};
    end = end || start;
    var monthsCount = differenceInCalendarMonths(end, start) + 1;
    var months = {};
    for (var m = 0; m < monthsCount; ++m) {
        var month = startOfMonth(addMonths(start, m));
        var startsInMonth = isSameMonth(month, start);
        var endsInMonth = isSameMonth(month, end);
        var startsBeforeMonth = !startsInMonth && isBefore(start, month);
        var endsAfterMonth = !endsInMonth && isAfter(end, month);
        var isMonthSelected = startsBeforeMonth && endsAfterMonth;
        var monthState = (_b = {},
            _b[prop] = isMonthSelected,
            _b.weeks = {},
            _b);
        if (!isMonthSelected) {
            var weekCount = differenceInCalendarWeeks(endsInMonth ? endOfWeek(end) : endOfMonth(month), startsInMonth ? startOfWeek(start) : month) + 1;
            var startWeek = startsInMonth ? startOfWeek(start) : month;
            for (var w = 0; w < weekCount; ++w) {
                var week = startOfWeek(addWeeks(startWeek, w));
                if (isBefore(week, month)) {
                    week = month;
                }
                var startsInWeek = isSameWeek(week, start);
                var endsInWeek = isSameWeek(week, end);
                var startsBeforeWeek = !startsInWeek && isBefore(start, week);
                var endsAfterWeek = !endsInWeek && isAfter(end, week);
                var isWeekSelected = startsBeforeWeek && endsAfterWeek;
                var weekState = (_c = {},
                    _c[prop] = isWeekSelected,
                    _c.days = {},
                    _c);
                if (!isWeekSelected) {
                    var weekEnd = endsInWeek ? end : endOfWeek(week);
                    var startDay = startsInWeek ? start : startOfWeek(week);
                    if (isAfter(weekEnd, endOfMonth(month))) {
                        weekEnd = startOfDay(endOfMonth(month));
                    }
                    if (isBefore(startDay, month)) {
                        startDay = month;
                    }
                    var dayCount = differenceInCalendarDays(weekEnd, startsInWeek ? startDay : week) +
                        1;
                    for (var d = 0; d < dayCount; ++d) {
                        var day = startOfDay(addDays(startDay, d));
                        var dayState = (_d = {},
                            _d[prop] = true,
                            _d.isRangeStart = isSameDay(day, start),
                            _d.isRangeEnd = isSameDay(day, end),
                            _d);
                        weekState.days[day.valueOf()] = dayState;
                    }
                }
                monthState.weeks[week.valueOf()] = weekState;
            }
        }
        months[month.valueOf()] = monthState;
    }
    return months;
}
